import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchProject, updateProjectText } from '../../shared/api/textProjectApi';
import { useAppDispatch } from '../../store/store';
import { fetchImageProject, updateLayoutImage } from '../../shared/api/imageProjectApi';
import s from './CustomPostPage.module.css';
import LogoComponent from '../../shared/ui/logo/LogoComponent';
import Button from '../../shared/ui/button/Button';
import PostEditorTabs from './widgets/post-line/PostEditorTabs';
import { CanvasContainerContext } from '../../shared/context/CanvasContainerContext';
import Canvas from '../image-editor-page/widgets/canvas/Canvas';
import InputGenerate from './widgets/input-generate/InputGenerate';
import DisplayEditSettings from './widgets/display-edit-settings/DisplayEditSettings';
import ChooseToolPanel from './widgets/choose-tool-panel/ChooseToolPanel';
import { useSelector } from 'react-redux';
import { selectActiveEditorMode, selectActivePrompt } from '../../store/selectors/pageSelectors';
import { modules } from '../text-editor-page/quill/quillConfig';
import { StyledReactQuill } from '../text-editor-page/quill/StyledReactQuill';
import {
    selectCurrentProject,
    selectScriptData,
    selectIsLoadingScript,
    selectIsRefining,
    selectIsSearchingReferences,
    selectImageData,
} from '../../store/selectors/projectSelectors';
import { formatScriptData } from '../../shared/helpers/transformData';
import { useDebounce } from '../../shared/hooks/useDebounce';
import Loader from '../../shared/ui/loader/Loader';
import {
    selectImageLayout,
    selectLoading,
    selectLoadingDownload,
    selectSelectedImage,
} from '../../store/selectors/imageSelectors';
import { setImageProject, setSelectedImage } from '../../store/slices/imageSlice';
import { quillRef } from '../../store/ref/quillRef';

import PreviewContainer from './widgets/preview/PreviewContainer';
import { selectImageProject } from '../../store/selectors/imageSelectors';
import { selectUser } from '../../store/selectors/authSelectors';
import { useDebouncedSaveImage } from '../../shared/hooks/useDebouncedSaveImage';
import UndoButton from '../../shared/ui/undo-button/UndoButton';
import TextToolPanel from './widgets/text-adjust/TextAdjust';
import UserProfileButton from '../../shared/ui/user-profile-button/UserProfileButton';
import { setActiveEditorMode, toggleActivePrompt } from '../../store/slices/pageSlice';
import {
    setCurrentProject,
    setImageData,
    setSelectedSocialMedia,
    setVideoData,
} from '../../store/slices/projectSlice';
import { setScriptData } from '../../store/slices/projectSlice';
import { setProjectNotes } from '../../store/slices/notesSlice';
import { selectVideo } from '../../store/selectors/projectSelectors';
import SaveFrameLoader from '../../shared/ui/loader/SaveFrameLoader';
import { uploadCanvasImage } from '../../shared/helpers/downloadCanvas';

import { setHasChanged } from '../../store/slices/imageSlice';
import { selectHasChanged } from '../../store/selectors/imageSelectors';
import { handleSaveImage } from '../../shared/utils/handleSaveImage';
import PostButton from '../../shared/ui/post-button/PostButton';

const CustomPostPage = () => {
    const [newImageWithEffects, setNewImageWithEffects] = useState<File | null>(null);

    const { projectId } = useParams();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [value, setValue] = useState<string>('');

    const containerRef = useRef<HTMLDivElement>(null);
    const isFirstDebounce = useRef(true);
    const activeEditorMode = useSelector(selectActiveEditorMode);
    const scriptData = useSelector(selectScriptData);
    const currentProject = useSelector(selectCurrentProject);
    const imageProject = useSelector(selectImageProject);
    const imageLayout = useSelector(selectImageLayout);
    const debouncedValue = useDebounce(value, 2000);
    const selectedImage = useSelector(selectSelectedImage);
    const user = useSelector(selectUser);
    const activePrompt = useSelector(selectActivePrompt);
    const loadingDownload = useSelector(selectLoadingDownload);
    const isLoadingScript = useSelector(selectIsLoadingScript);
    const isLoadingRefine = useSelector(selectIsRefining);
    const isLoadingReferences = useSelector(selectIsSearchingReferences);
    const loadingImage = useSelector(selectLoading);
    const projectVideo = useSelector(selectVideo);
    const imageData = useSelector(selectImageData);
    const [isLoadingSaveFrameBeforeBack, setIsLoadingSaveFrameBeforeBack] = useState(false);
    const [isLoadingSaveFrameBeforeReview, setIsLoadingSaveFrameBeforeReview] = useState(false);
    const [isLoadingSaveFrameAfterGenerate, setIsLoadingSaveFrameAfterGenerate] = useState(false);

    const hasChanged = useSelector(selectHasChanged);

    useDebouncedSaveImage({
        activePrompt,
        selectedImage,
        dispatch,
        newImageWithEffects,
    });

    useEffect(() => {
        if (
            imageLayout &&
            projectId &&
            imageProject &&
            imageProject.allImagesForLayouts.length === 1
        ) {
            const imageData = imageProject?.allImagesForLayouts[0];

            if (imageData) {
                dispatch(setSelectedImage({ image: imageData, layoutId: imageLayout.id }));
            }
        }
    }, [dispatch, imageProject]);

    useEffect(() => {
        if (projectId) {
            const fetchData = async () => {
                const project = await dispatch(fetchProject({ projectId: projectId })).unwrap();

                const imageProjectId = project.image?.id;
                if (imageProjectId) {
                    await dispatch(fetchImageProject(imageProjectId)).unwrap();
                }
            };
            fetchData();
        }
    }, [dispatch, projectId]);

    useEffect(() => {
        if (scriptData) {
            (async () => {
                const formattedText = await formatScriptData(scriptData);
                setValue(formattedText);
            })();
        }
    }, [scriptData]);

    const handleSetValue = (value: string) => {
        setValue(value);
    };

    const mediaType = selectedImage?.image?.url ? 'image' : projectVideo?.url ? 'video' : 'image';

    useEffect(() => {
        if (debouncedValue.trim() === '' || !currentProject?.id) {
            return;
        }
        if (isFirstDebounce.current) {
            isFirstDebounce.current = false;
            return;
        }
        dispatch(updateProjectText({ projectId: currentProject.id, body: debouncedValue }));
    }, [debouncedValue, currentProject?.id, dispatch]);

    useEffect(() => {
        return () => {
            dispatch(toggleActivePrompt(null));
            dispatch(setSelectedImage({ image: null, layoutId: null }));
            dispatch(setCurrentProject(null));
            dispatch(setImageData(null));
            dispatch(setScriptData(null));
            dispatch(setProjectNotes([]));
            dispatch(setImageProject(null));
            dispatch(toggleActivePrompt(null));
            dispatch(setSelectedSocialMedia(null));
            dispatch(setVideoData(null));
            dispatch(setActiveEditorMode('Visual'));
        };
    }, []);

    useEffect(() => {
        if (activeEditorMode === 'Visual') {
            if (selectedImage?.image) {
                dispatch(toggleActivePrompt('effects'));
            } else {
                dispatch(toggleActivePrompt(null));
            }
        }
        if (activeEditorMode === 'Captions') {
            dispatch(toggleActivePrompt('tone_and_voice'));
        }
    }, [dispatch, activeEditorMode]);

    const backNavigation = async () => {
        await saveImage({ source: 'back' });
        dispatch(toggleActivePrompt(null));
        navigate(-1);
    };

    const saveImage = async ({ source }: { source: 'review' | 'back' | 'generate' }) => {
        if (hasChanged || source === 'generate') {
            console.log('hasChanged', hasChanged);
            console.log('source', source);
            console.log('selectedImage', selectedImage);

            if (source === 'back') {
                setIsLoadingSaveFrameBeforeBack(true);
            }

            if (source === 'review') {
                setIsLoadingSaveFrameBeforeReview(true);
            }

            // if (source === 'generate') {
            //     setIsLoadingSaveFrameAfterGenerate(true);
            // }

            dispatch(setHasChanged(false));

            if (selectedImage?.image) {
                console.log('selectedImage?.image', selectedImage?.image);

                if (containerRef.current && imageLayout?.id) {
                    const canvasEl = containerRef.current.querySelector(
                        'canvas',
                    ) as HTMLCanvasElement;

                    console.log(canvasEl, 'canvasEl');
                    if (canvasEl) {
                        const file = await uploadCanvasImage(containerRef);
                        if (file) {
                            await dispatch(updateLayoutImage({ layoutId: imageLayout.id, file }));
                        }
                    }
                }
            }

            if (source === 'back') {
                setIsLoadingSaveFrameBeforeBack(false);
            }

            if (source === 'review') {
                setIsLoadingSaveFrameBeforeReview(false);
            }

            // if (source === 'generate') {
            //     setIsLoadingSaveFrameAfterGenerate(false);
            // }
        } else {
            return;
        }
    };

    return imageProject !== null ? (
        <CanvasContainerContext.Provider value={containerRef}>
            <div className={s.container}>
                <div className={s.top_container}>
                    <LogoComponent />
                    <div className={s.top_right_container}>
                        {activeEditorMode !== 'Review' && (
                            <InputGenerate
                                saveImage={() => saveImage({ source: 'generate' })}
                                activeEditorMode={activeEditorMode}
                                currentProject={currentProject}
                                imageProject={imageProject}
                                selectedImage={selectedImage}
                            />
                        )}
                        <UserProfileButton />
                    </div>
                </div>
                <div className={s.center_container}>
                    <div className={s.left_center_container}>
                        <h3 className={s.title}>Customize your post</h3>
                        <p className={s.subtitle}>
                            Every edit brings your brand into sharper focus.
                        </p>

                        <PostEditorTabs
                            activeEditorMode={activeEditorMode}
                            saveImage={() => saveImage({ source: 'review' })}
                        />
                        <div className={s.tools_wrapper}>
                            {(selectedImage?.image || activeEditorMode === 'Captions') &&
                                activeEditorMode !== 'Review' && <ChooseToolPanel />}
                            {selectedImage?.image && <UndoButton />}
                        </div>

                        <TextToolPanel />
                        <DisplayEditSettings />
                    </div>
                    <div className={s.right_center_container}>
                        {activeEditorMode === 'Visual' && (
                            <div className={s.canvas_container}>
                                <Canvas
                                    ref={containerRef}
                                    setNewImageWithEffects={setNewImageWithEffects}
                                />
                                {loadingImage && (
                                    <Loader
                                        text="Our AI is customizing your image..."
                                        blur={true}
                                    />
                                )}
                                {isLoadingSaveFrameBeforeReview && (
                                    <Loader text="We need to save your post..." blur={true} />
                                )}
                                {isLoadingSaveFrameAfterGenerate && (
                                    <Loader text="We need to save your image..." blur={true} />
                                )}
                            </div>
                        )}
                        {activeEditorMode === 'Captions' && (
                            <div className={s.quill_container}>
                                <StyledReactQuill
                                    ref={quillRef}
                                    value={value}
                                    onChange={handleSetValue}
                                    modules={modules}
                                    className={`${s.quill}`}
                                />
                                {isLoadingRefine && (
                                    <Loader text="Our AI is refining your text...." blur={true} />
                                )}
                                {isLoadingScript && (
                                    <Loader text="Our AI is customizing your text..." blur={true} />
                                )}
                                {isLoadingReferences && (
                                    <Loader
                                        text="We are looking for references on the web..."
                                        blur={true}
                                    />
                                )}

                                {/* <WordsCount value={value} quillRef={quillRef} /> */}
                            </div>
                        )}
                        {activeEditorMode === 'Review' && (
                            <div className={s.review_container}>
                                <PreviewContainer
                                    avatarUrl={user?.profile_picture ?? ''}
                                    userName={user?.name || user?.username || user?.email || ''}
                                    text={value}
                                    mediaUrl={projectVideo?.url || imageData?.url}
                                    userHandle={'mariia'}
                                    mediaType={mediaType}
                                />
                                {isLoadingScript && (
                                    <Loader text="Changing your social media..." blur={true} />
                                )}
                            </div>
                        )}
                    </div>
                </div>
                <div className={s.bottom_container}>
                    <Button
                        text="Back"
                        onClick={backNavigation}
                        style={{
                            width: '100px',
                            fontWeight: '600',
                        }}
                    />

                    <PostButton
                        currentProject={currentProject}
                        activeEditorMode={activeEditorMode}
                        user={user}
                    />
                </div>
            </div>

            {loadingDownload && <Loader />}
            {isLoadingSaveFrameBeforeBack && <SaveFrameLoader />}
        </CanvasContainerContext.Provider>
    ) : (
        <Loader />
    );
};

export default CustomPostPage;
